import type { MantineNumberSize } from '@mantine/core';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import { useId } from '@mantine/hooks';
import * as Plot from '@observablehq/plot';
import { Icon } from '@repo/foundations';
import { Text } from '@repo/foundations/components/Text/Text';
import { svg } from 'htl';
import { isNil, max } from 'lodash-es';
import { transparentize } from 'polished';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartTooltip } from '../../components/Chart/components/ChartTooltip';
import { captureError } from '../../web-tracing';
import { pointerX } from './TooltipMark/pointer';
import { cleanUpTooltips, tooltip } from './TooltipMark/tooltipMark';
import { getPointValueFromChannel } from './utils';

/**
 * Configuration for a single data channel/series in the multi-area line chart.
 * Each channel represents a line (with optional area) on the chart.
 */
export interface StackedLineChartChannel<T, X = Date, Y = number> {
	label: string; // Display name for this data series in the legend
	color: string; // Color for line and area
	x: keyof T | ((d: T) => X); // X-axis data accessor
	y: keyof T | ((d: T) => Y); // Y-axis data accessor
	renderArea?: boolean; // Whether to show filled area beneath line
	areaOpacity?: number; // Opacity for filled area (0-1)
	lineWidth?: number; // Width of line in pixels
	dashArray?: string; // SVG dash pattern (e.g. "5,5" for dashed line)
	zIndex?: number; // Controls layer order (higher renders on top)
}

/**
 * Configuration for X and Y axes in the chart.
 * Controls appearance, tick formatting, and visibility of the axis.
 */
export interface StackedLineChartAxis<P> {
	formatter?: (point: P) => string; // Formats axis tick labels
	tooltipFormatter?: (point: P) => string; // Formats tooltip values
	maxTicks?: number; // Maximum number of tick marks
	visible?: boolean; // Show/hide axis
	grid?: boolean; // Show/hide grid lines
}

/**
 * Configuration for the chart legend.
 * Controls visibility, positioning, and appearance of the legend.
 */
export interface LegendProps {
	position?: 'top' | 'bottom'; // Legend placement
	visible?: boolean; // Show/hide legend
	itemSize?: number; // Size of color indicators in pixels
	columns?: number; // Number of columns for legend items
}

/**
 * Parameters for rendering tooltips in the chart.
 * Contains tooltip position and the data index being hovered.
 */
type RenderTooltipParams = {
	readonly x: number; // X coordinate for tooltip positioning
	readonly y: number; // Y coordinate for tooltip positioning
	readonly dataIndex: number[] | null; // Index of data point or null
};

/**
 * Props for the StackedLineChart component.
 * Provides complete configuration for the chart's appearance and behavior.
 */
export interface StackedLineChartProps<T, X = Date, Y = number> {
	channels: Array<StackedLineChartChannel<T, X, Y>>; // Data series to display
	data: Array<T>; // Source data objects
	labelX?: string; // X-axis label
	labelY?: string; // Y-axis label
	width?: MantineNumberSize; // Chart width (CSS or pixels)
	height?: MantineNumberSize; // Chart height (CSS or pixels)
	xAxis?: StackedLineChartAxis<X>; // X-axis configuration
	yAxis?: StackedLineChartAxis<Y>; // Y-axis configuration
	tooltipRenderer?: (dataIndex: number) => React.ReactNode; // Custom tooltip content generator
	chartProps?: Partial<Plot.PlotOptions>; // Additional Plot.js options
	legend?: LegendProps; // Legend configuration
	maxChannels?: number; // Maximum number of lines to display
	showEmptyStateMessage?: boolean; // Message shown when no data is available
	emptyStateMessage?: {
		title: string;
		description: string;
	}; // Message shown when no data is available
	onTooltipChange?: (
		dataIndex: number | null,
		position: { x: number; y: number } | null
	) => void; // Callback when tooltip changes
	onClick?: (dataIndex: number) => void; // Callback when chart is clicked
}

/**
 * Plot.js element type with remove method and custom click handler.
 * Used for proper cleanup of the chart.
 */
type PlotElement = SVGSVGElement & {
	remove: () => void;
	clickHandler?: (event: MouseEvent) => void;
};

/**
 * Calculates the next X position by extrapolating from the last two points.
 * Handles both Date and numeric X values.
 */
function calculateNextXPosition(secondLastX: unknown, lastX: unknown): unknown {
	if (lastX instanceof Date && secondLastX instanceof Date) {
		const timeDelta = lastX.getTime() - secondLastX.getTime();
		return new Date(lastX.getTime() + timeDelta);
	}

	if (typeof lastX === 'number' && typeof secondLastX === 'number') {
		const numericDelta = lastX - secondLastX;
		return lastX + numericDelta;
	}

	// Fallback: return the last X value (no extension)
	return lastX;
}

/**
 * Creates a dashed line extension for incomplete data visualization.
 * This shows where the trend would continue if more data were available.
 */
function createIncompleteDataExtension<T, X, Y>(
	data: T[],
	channel: StackedLineChartChannel<T, X, Y>
): Plot.Markish | null {
	// Need at least 2 data points to calculate trend
	if (data.length < 2) return null;

	try {
		// Extract X and Y values for the last two data points
		const lastTwoPoints = data.slice(-2);
		const [secondLastPoint, lastPoint] = lastTwoPoints;

		const secondLastX = getPointValueFromChannel(channel.x, secondLastPoint);
		const lastX = getPointValueFromChannel(channel.x, lastPoint);
		const lastY = getPointValueFromChannel(channel.y, lastPoint);

		// Calculate the next X position by extrapolating the trend
		const nextX = calculateNextXPosition(secondLastX, lastX);

		// Create data points for the extension line
		const extensionData = [
			{ x: lastX, y: lastY },
			{ x: nextX, y: lastY },
		];

		return Plot.line(extensionData, {
			x: 'x',
			y: 'y',
			stroke: channel.color,
			strokeWidth: channel.lineWidth ?? 1.5,
			strokeDasharray: channel.dashArray ?? '4,4',
			strokeOpacity: 0.7,
		});
	} catch (error) {
		captureError(error);
		return null;
	}
}

/**
 * Creates all incomplete data extensions for the chart channels.
 */
function createIncompleteDataExtensions<T, X, Y>(
	data: T[],
	channels: StackedLineChartChannel<T, X, Y>[]
): Plot.Markish[] {
	return channels
		.map((channel) => createIncompleteDataExtension(data, channel))
		.filter((extension): extension is Plot.Markish => extension !== null);
}

/**
 * StackedLineChart - A component for visualizing multiple data series as lines with optional area fills.
 *
 * This component uses Observable Plot to render a responsive chart with customizable appearance,
 * interactive tooltips, and accessibility features. It supports multiple data series with independent
 * styling, layering control, and legend display.
 */
export function StackedLineChart<T, X, Y>({
	channels,
	data,
	labelX,
	labelY,
	width = '100%',
	height = '100%',
	xAxis,
	yAxis,
	tooltipRenderer,
	chartProps,
	legend = { visible: true, position: 'bottom', itemSize: 16, columns: 4 },
	maxChannels = 4,
	showEmptyStateMessage = false,
	emptyStateMessage,
	onTooltipChange,
	onClick,
}: StackedLineChartProps<T, X, Y>) {
	const theme = useMantineTheme();
	const containerRef = useRef<HTMLDivElement>(null);
	const [chartWidth, setChartWidth] = useState<number | undefined>();
	const [error, setError] = useState<Error | null>(null);

	// Generate unique IDs for each gradient
	const baseId = useId();

	// Memoize channel IDs to prevent recalculation
	const channelIds = useMemo(
		() => channels.map((_, i) => `${baseId}-${i}`),
		[channels.length, baseId]
	);

	// Limit displayed channels to the maximum specified in props (default: 4)
	// This prevents performance issues and visual clutter with too many data series
	const limitedChannels = useMemo(
		() => channels.slice(0, maxChannels),
		[channels, maxChannels]
	);

	// Sort channels by zIndex to control rendering order (lower zIndex renders first)
	const sortedChannels = useMemo(
		() =>
			[...limitedChannels].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
		[limitedChannels]
	);

	// Memoize chart styles to prevent theme changes from triggering full re-renders
	const chartStyles = useMemo(
		() => ({
			axisColor: theme.other.getColor('text/secondary/default'),
			gridColor: theme.other.getColor('border/secondary/default'),
			margins: {
				top: 20,
				bottom: isNil(xAxis?.visible) || xAxis.visible ? 40 : 10,
				left: 60,
				right: 30,
			},
		}),
		[theme, xAxis?.visible]
	);

	// Reduces the number of dependencies that trigger chart re-rendering
	const chartConfig = useMemo(
		() => ({
			width: chartWidth,
			height: typeof height === 'number' ? height : undefined,
			xAxisVisible: isNil(xAxis?.visible) || xAxis.visible,
			yAxisVisible: isNil(yAxis?.visible) || yAxis.visible,
			xAxisGrid: xAxis?.grid,
			yAxisGrid: yAxis?.grid,
			xAxisMaxTicks: xAxis?.maxTicks,
			yAxisMaxTicks: yAxis?.maxTicks,
			hasTooltipRenderer: !!tooltipRenderer,
		}),
		[
			chartWidth,
			height,
			xAxis?.visible,
			yAxis?.visible,
			xAxis?.grid,
			yAxis?.grid,
			xAxis?.maxTicks,
			yAxis?.maxTicks,
			tooltipRenderer,
		]
	);

	const renderEmptyState = useCallback(
		() => (
			<Flex
				h={typeof height === 'number' ? height : 200}
				w="100%"
				justify="center"
				direction="column"
				align="center"
				style={{
					backgroundColor: theme.other.getColor('fill/primary/default'),
					borderRadius: theme.radius.md,
				}}
				role="region"
				aria-label="Empty chart state"
			>
				<Icon name="chartBar" size="lg" iconPadding="xs" />
				<Text size="sm" fw={500} color="text/primary/default">
					{emptyStateMessage?.title || 'No data available'}
				</Text>
				<Text size="sm" color="text/secondary/default">
					{emptyStateMessage?.description ||
						'Select a different time range or data source'}
				</Text>
			</Flex>
		),
		[height, theme, showEmptyStateMessage, emptyStateMessage]
	);

	const renderError = useCallback(
		() => (
			<Flex
				h={typeof height === 'number' ? height : 200}
				w="100%"
				justify="center"
				align="center"
				style={{
					backgroundColor: theme.other.getColor('fill/tertiary/default'),
					borderRadius: theme.radius.md,
				}}
				role="alert"
			>
				<Text color="text/critical/default">
					Error rendering chart: {error?.message}
				</Text>
			</Flex>
		),
		[height, theme, error]
	);

	const renderLegend = useCallback(() => {
		if (!legend.visible || limitedChannels.length === 0) return null;

		return (
			<Flex
				justify="center"
				gap="md"
				wrap="wrap"
				mt={legend.position === 'bottom' ? theme.spacing.xs : 0}
				mb={legend.position === 'top' ? theme.spacing.xs : 0}
				role="list"
				aria-label="Chart legend"
			>
				{limitedChannels.map((channel, index) => (
					<Flex
						key={channelIds[index]}
						align="center"
						justify="center"
						gap="xs"
						mb={theme.spacing.xs}
						role="listitem"
					>
						<Flex align="center" gap={theme.spacing.xs}>
							<Box
								w={legend.itemSize ?? 10}
								h={2}
								style={{
									backgroundColor: channel.color,
									borderRadius: theme.radius.sm,
									opacity: channel.renderArea ? 0.7 : 1,
									border: channel.dashArray
										? `1px solid ${channel.color}`
										: 'none',
									backgroundImage: channel.dashArray
										? `
                  linear-gradient(90deg,
                  ${channel.color} 50%,
                  transparent 50%)
                `
										: 'none',
									backgroundSize: channel.dashArray ? '8px 100%' : 'auto',
								}}
								aria-hidden="true"
							/>
							<Text color="text/secondary/default" size="xs">
								{channel.label}
							</Text>
						</Flex>
					</Flex>
				))}
			</Flex>
		);
	}, [limitedChannels, channelIds, legend, theme]);

	/**
	 * Updates the chart width when the container size changes.
	 * Used by the resize observer to maintain responsive behavior.
	 */
	const updateChartWidth = useCallback(() => {
		if (containerRef.current) {
			setChartWidth(containerRef.current.clientWidth);
		}
	}, []);

	// Set up resize observer to maintain responsive chart dimensions
	useEffect(() => {
		updateChartWidth();

		const resizeObserver = new ResizeObserver(() => {
			updateChartWidth();
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [updateChartWidth]);

	const renderTooltip = useCallback(
		(params: RenderTooltipParams) => {
			const { x, y, dataIndex } = params;
			if (!dataIndex) return null;

			const [indexValue] = dataIndex;
			if (!data[indexValue] || !tooltipRenderer) return null;

			return (
				<ChartTooltip x={x + 8} y={y} w="auto" miw={260}>
					<Box>{tooltipRenderer(indexValue)}</Box>
				</ChartTooltip>
			);
		},
		[data, tooltipRenderer]
	);

	// This effect creates and updates the chart based on props changes
	useEffect(() => {
		if (!containerRef.current || data.length === 0) return undefined;

		let plot: PlotElement | null = null;

		try {
			// Calculate max Y value across all series for gradient scaling
			const maxY =
				max(
					data.map((item) =>
						max(
							sortedChannels.map((channel) => {
								try {
									return getPointValueFromChannel(channel.y, item);
								} catch (err) {
									captureError(err);
									return 0;
								}
							})
						)
					)
				) || 0;

			const { axisColor, gridColor, margins } = chartStyles;
			const {
				width: plotWidth,
				height: plotHeight,
				xAxisVisible,
				yAxisVisible,
				xAxisGrid,
				yAxisGrid,
				xAxisMaxTicks,
				yAxisMaxTicks,
				hasTooltipRenderer,
			} = chartConfig;

			// Create the Plot.js chart with all configuration options
			plot = Plot.plot({
				// Chart dimensions and margins
				width: plotWidth,
				height: plotHeight,
				marginTop: margins.top,
				marginBottom: margins.bottom,
				marginLeft: margins.left,
				marginRight: margins.right,
				style: {
					background: 'transparent',
					overflow: 'visible',
				},
				// X-axis configuration
				x: {
					ticks: xAxisVisible ? xAxisMaxTicks : 0,
					label: labelX ?? null,
					nice: true,
					grid: xAxisGrid,
					tickFormat: xAxis?.formatter,
				},
				// Y-axis configuration
				y: {
					ticks: yAxisVisible ? yAxisMaxTicks : 0,
					label: labelY ?? null,
					grid: yAxisGrid,
					tickFormat: yAxis?.formatter,
				},
				// All visual elements to display in the chart
				marks: [
					// Grid lines (if enabled)
					...(xAxisGrid
						? [
								Plot.gridX({
									stroke: gridColor,
									strokeOpacity: 0.2,
								}),
							]
						: []),
					...(yAxisGrid
						? [
								Plot.gridY({
									stroke: gridColor,
									strokeOpacity: 0.2,
								}),
							]
						: []),

					// Axis (if visible)
					...(xAxisVisible
						? [
								Plot.axisX({
									anchor: 'bottom',
									label: labelX ?? null,
									labelAnchor: 'center',
									labelOffset: 30,
									ticks: xAxisMaxTicks,
									tickSize: 0,
									fontSize: 12,
									color: axisColor,
									tickPadding: 12,
									textAnchor: 'middle',
									tickFormat: xAxis?.formatter,
								}),
							]
						: []),
					...(yAxisVisible
						? [
								Plot.axisY({
									anchor: 'left',
									label: labelY ?? null,
									labelAnchor: 'center',
									labelOffset: 40,
									ticks: yAxisMaxTicks,
									tickSize: 0,
									fontSize: 12,
									color: axisColor,
									tickPadding: 16,
									tickFormat: yAxis?.formatter,
								}),
							]
						: []),

					// First render all areas (so they appear behind the lines)
					// This ensures proper layering of visual elements
					...sortedChannels.flatMap((channel, index) => {
						if (channel.renderArea) {
							const opacity = channel.areaOpacity ?? 0.5;

							try {
								return [
									// Area fill below the line
									Plot.areaY(data, {
										x: channel.x as Plot.ChannelValueSpec,
										y: channel.y as Plot.ChannelValueSpec,
										fill: `url(#gradient-${channelIds[index]})`,
										opacity,
									}),
									// SVG gradient definition for the area fill
									function GradientPlot(_idx, { y }) {
										return svg`<defs>
                  <linearGradient id="gradient-${channelIds[index]}" gradientUnits="userSpaceOnUse"
                    x1=0 x2=0 y1=${y?.(0)} y2=${y?.(maxY)}>
                      <stop offset=0% stop-color=${transparentize(1, channel.color)} />
                      <stop offset=100% stop-color=${transparentize(0.3, channel.color)} />
                  </linearGradient>
                </defs>`;
									},
								] as Plot.Markish[];
							} catch (err) {
								captureError(err);
								return [];
							}
						}
						return [];
					}),

					// Then render all lines (so they appear on top of areas)
					...sortedChannels.map((channel) => {
						try {
							return Plot.line(data, {
								x: channel.x as Plot.ChannelValueSpec,
								y: channel.y as Plot.ChannelValueSpec,
								stroke: channel.color,
								strokeWidth: channel.lineWidth ?? 1.5,
								strokeDasharray: channel.dashArray,
							});
						} catch (err) {
							captureError(err);
							return Plot.line([], {}); // Return empty line as fallback
						}
					}),
					...createIncompleteDataExtensions<T, X, Y>(data, sortedChannels),
					// Add the vertical rule for tooltips (follows mouse position)
					Plot.ruleX(
						data,
						pointerX({
							x: sortedChannels[0].x as Plot.ChannelValueSpec,
							stroke: gridColor,
						})
					),

					// Add dots at the intersection of the vertical rule and each line
					...(hasTooltipRenderer
						? sortedChannels
								.map((channel) => {
									try {
										return Plot.dot(
											data,
											pointerX({
												x: channel.x as Plot.ChannelValueSpec,
												y: channel.y as Plot.ChannelValueSpec,
												fill: channel.color,
												stroke: 'white',
												strokeWidth: 1.5,
												r: 4.5,
											})
										);
									} catch (err) {
										captureError(err);
										return null;
									}
								})
								.filter(Boolean) // Filter out null values
						: []),

					// Add tooltips if tooltip renderer is provided
					...(hasTooltipRenderer
						? [
								tooltip(
									data,
									pointerX({
										x: sortedChannels[0].x as Plot.ChannelValueSpec,
										tooltipRenderer: renderTooltip,
										onTooltipChange: onTooltipChange,
									})
								),
							]
						: []),
				],
				// Apply any additional chart props provided by the consumer
				...chartProps,
			}) as PlotElement;

			// Safely add the plot to the DOM
			if (containerRef.current) {
				containerRef.current.innerHTML = ''; // Clear previous chart
				containerRef.current.appendChild(plot);

				// Add click event listener if onClick handler is provided
				if (onClick && plot) {
					const handleClick = (event: MouseEvent) => {
						if (!plot) return;

						// Find the closest data point to the click
						const rect = plot.getBoundingClientRect();
						const x = event.clientX - rect.left;

						// Account for chart margins when calculating click position
						const { left, right } = margins;
						const chartContentWidth = rect.width - left - right;
						const clickX = x - left;

						// Only process clicks within the chart content area
						if (clickX >= 0 && clickX <= chartContentWidth) {
							const dataLength = data.length;
							const clickRatio = clickX / chartContentWidth;
							const dataIndex = Math.round(clickRatio * (dataLength - 1));

							// Ensure the index is within bounds
							if (dataIndex >= 0 && dataIndex < dataLength) {
								onClick(dataIndex);
							}
						}
					};

					plot.addEventListener('click', handleClick);

					// Store the handler for cleanup
					plot.clickHandler = handleClick;
				}
			}
		} catch (err) {
			// Handle errors during chart creation
			setError(err instanceof Error ? err : new Error(String(err)));
			captureError(err);
			return undefined; // Return early if there's an error
		}

		// Return cleanup function to prevent memory leaks
		return () => {
			try {
				if (plot) {
					// Remove click event listener if it exists
					if (plot.clickHandler) {
						plot.removeEventListener('click', plot.clickHandler);
					}
					plot.remove();
				}
				cleanUpTooltips();
			} catch (err) {
				captureError(err);
			}
		};
	}, [
		chartConfig,
		data,
		sortedChannels,
		chartStyles,
		labelX,
		labelY,
		xAxis?.formatter,
		yAxis?.formatter,
		renderTooltip,
		channelIds,
		chartProps,
		onTooltipChange,
		onClick,
		error,
	]);

	// Show error state if there was an error rendering the chart
	if (error) return renderError();

	// Show empty state if there's no data to display
	if (data.length === 0 || showEmptyStateMessage) return renderEmptyState();

	return (
		<Box
			role="figure"
			aria-label={`${labelY || 'Value'} vs ${labelX || 'Time'} chart`}
			data-testid="multi-area-line-chart-container"
		>
			{legend.position === 'top' && renderLegend()}
			<Box
				w={width}
				h={height}
				ref={containerRef}
				data-testid="multi-area-line-chart"
				aria-busy={!chartWidth}
			/>
			{legend.position === 'bottom' && renderLegend()}
		</Box>
	);
}

export const MemoizedStackedLineChart = memo(
	StackedLineChart
) as typeof StackedLineChart;
